import { isPrintableAscii, type KeyboardEventLike } from './chord';
import type { KeyboardLayoutSnapshot } from './keybindingTypes';

type BrowserLayoutMap = {
  get: (code: string) => string | undefined;
  forEach: (callback: (value: string, code: string) => void) => void;
};

type NavigatorLike = {
  readonly keyboard?: {
    getLayoutMap: () => Promise<BrowserLayoutMap>;
  };
};

type WindowLike = {
  addEventListener: (type: 'focus' | 'keydown', listener: (event: unknown) => void) => void;
  removeEventListener: (type: 'focus' | 'keydown', listener: (event: unknown) => void) => void;
};

export type LayoutMapCache = {
  readonly getSnapshot: () => KeyboardLayoutSnapshot;
  readonly subscribe: (listener: (snapshot: KeyboardLayoutSnapshot) => void) => () => void;
  readonly refresh: () => Promise<KeyboardLayoutSnapshot>;
  readonly observeKeydown: (event: KeyboardEventLike) => void;
  readonly start: () => void;
  readonly dispose: () => void;
};

export type LayoutMapCacheOptions = {
  readonly navigatorObject?: NavigatorLike;
  readonly windowObject?: WindowLike;
};

const EMPTY_LAYOUT = new Map<string, string>();

function copyLayoutMap(source: BrowserLayoutMap): ReadonlyMap<string, string> {
  const entries = new Map<string, string>();
  source.forEach((value, code) => entries.set(code, value));
  return entries;
}

function comparableKey(value: string): string {
  return /^[a-z]$/i.test(value) ? value.toUpperCase() : value;
}

/**
 * An unmodified keystroke, whose `key` is the map's unshifted character.
 * AltGr is excluded too: Linux reports it without `altKey`, and AltGr+ß types
 * '\' on a German layout, which proves nothing about the map (FR10).
 */
function isPlainKeystroke(event: KeyboardEventLike): boolean {
  return !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey &&
    !event.isComposing && event.key !== 'Dead' && Boolean(event.code) &&
    !event.getModifierState?.('AltGraph');
}

function sameEntries(
  left: ReadonlyMap<string, string>,
  right: ReadonlyMap<string, string>,
): boolean {
  if (left.size !== right.size) return false;
  for (const [code, value] of left) {
    if (right.get(code) !== value) return false;
  }
  return true;
}

export function createLayoutMapCache(options: LayoutMapCacheOptions = {}): LayoutMapCache {
  const navigatorObject = options.navigatorObject ?? (globalThis.navigator as NavigatorLike | undefined);
  const windowObject = options.windowObject ?? (globalThis.window as unknown as WindowLike | undefined);
  let snapshot: KeyboardLayoutSnapshot = { status: 'loading', entries: EMPTY_LAYOUT, revision: 0 };
  let requestRevision = 0;
  let started = false;
  /** The newest getLayoutMap request still pending; a keystroke never starts a second one (FR12). */
  let inFlight: Promise<KeyboardLayoutSnapshot> | null = null;
  /**
   * `code` + typed key pairs a request already answered with the same
   * mismatching entry (FR12). On Windows the map is the FIRST ASCII-capable
   * layout, so typing on another one (US keys against a German map: '-' at
   * Minus, cached 'ß') mismatches for good and must not ask again on every
   * keystroke. Cleared when a new map is published and on every focus or
   * explicit refresh, so a real layout switch is still caught: by focus, or by
   * the first keystroke whose pair is not known yet.
   */
  const knownMismatches = new Set<string>();
  const listeners = new Set<(next: KeyboardLayoutSnapshot) => void>();

  const publish = (status: KeyboardLayoutSnapshot['status'], entries: ReadonlyMap<string, string>) => {
    if (snapshot.status === status && sameEntries(snapshot.entries, entries)) return snapshot;
    snapshot = { status, entries, revision: snapshot.revision + 1 };
    knownMismatches.clear();
    listeners.forEach((listener) => listener(snapshot));
    return snapshot;
  };

  const request = async (): Promise<KeyboardLayoutSnapshot> => {
    const revision = ++requestRevision;
    const getLayoutMap = navigatorObject?.keyboard?.getLayoutMap;
    if (!getLayoutMap) return publish('unavailable', EMPTY_LAYOUT);
    try {
      // Chromium intentionally exposes an ASCII-capable layout: first installed
      // ASCII layout on Windows, current ASCII input source on macOS.
      const nextEntries = copyLayoutMap(await getLayoutMap.call(navigatorObject.keyboard));
      if (revision !== requestRevision) return snapshot;
      return publish('ready', nextEntries);
    } catch {
      if (revision !== requestRevision) return snapshot;
      return publish('unavailable', EMPTY_LAYOUT);
    }
  };

  const requestLayoutMap = (): Promise<KeyboardLayoutSnapshot> => {
    const pending = request();
    inFlight = pending;
    const settled = () => { if (inFlight === pending) inFlight = null; };
    void pending.then(settled, settled);
    return pending;
  };

  /** Focus, start and callers: always a new request (the newest one wins) and a clean back-off. */
  const refresh = (): Promise<KeyboardLayoutSnapshot> => {
    knownMismatches.clear();
    return requestLayoutMap();
  };

  // Only a printable ASCII keystroke can prove the cache stale: a non-ASCII
  // key (Cyrillic typing against an ASCII-capable map) never refreshes, so
  // typing cannot storm getLayoutMap. It is compared with the cached entry
  // whether that entry is ASCII or not, so switching out of a non-Latin
  // layout without a blur (Russian → German) refreshes too (review FR10):
  // a stale non-Latin map would keep its US-key stand-ins active. A mismatch
  // reuses a pending request, and a pair the answer still contradicts is not
  // asked about again (FR12, `knownMismatches`).
  const observeKeydown = (event: KeyboardEventLike): void => {
    if (snapshot.status !== 'ready' || !isPlainKeystroke(event) ||
        !isPrintableAscii(event.key)) return;
    const cached = snapshot.entries.get(event.code);
    if (!cached) return;
    const typed = comparableKey(event.key);
    const pair = `${event.code}\n${typed}`;
    if (comparableKey(cached) === typed || knownMismatches.has(pair)) return;
    void (inFlight ?? requestLayoutMap()).then(() => {
      const answered = snapshot.entries.get(event.code);
      if (answered && comparableKey(answered) !== typed) knownMismatches.add(pair);
    });
  };

  const focusListener = (): void => { void refresh(); };
  const keydownListener = (event: unknown): void => observeKeydown(event as KeyboardEventLike);

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    refresh,
    observeKeydown,
    start: () => {
      if (started) return;
      started = true;
      windowObject?.addEventListener('focus', focusListener);
      windowObject?.addEventListener('keydown', keydownListener);
      void refresh();
    },
    dispose: () => {
      if (!started) return;
      started = false;
      windowObject?.removeEventListener('focus', focusListener);
      windowObject?.removeEventListener('keydown', keydownListener);
    },
  };
}
